import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AvailabilityDetailsDialog } from '@apps/web/components/caregivers/caregiver_availability_details';
import { AvailabilityMonthGrid } from '@apps/web/components/caregivers/caregiver_availability_month_grid';
import {
    buildEffectiveAvailabilityItems,
    buildTimeboxedDayGroups,
    findAvailabilitySegmentById,
    type TimelineItem,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { CaregiverShiftDetailsDialog } from '@apps/web/components/caregivers/caregiver_shift_details_dialog';
import { ProfileNotesPanel } from '@apps/web/components/entity_profile/profile_notes_panel';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    addMonthsToDateKey,
    endOfMonthDateKey,
    formatMonthKey,
    startOfMonthDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import { useCaregiverAvailabilityProfile } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type CaregiverOverviewProps = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly caregiverFirstName: string | null;
    readonly timezone: string | null;
    readonly externalSourceLabel?: string;
};

/**
 * Landing section of the caregiver popup: one month grid merging
 * availability windows and assigned shifts, with notes beside it — the
 * "can they take this shift?" view.
 */
export function CaregiverOverview({
    organizationId,
    caregiverId,
    caregiverFirstName,
    timezone,
    externalSourceLabel,
}: CaregiverOverviewProps) {
    const todayKey = useMemo(
        () => (timezone ? toDateKey(new Date(), timezone) : null),
        [timezone],
    );
    // Null until the user navigates; the visible month then follows today.
    const [anchorOverrideKey, setAnchorOverrideKey] = useState<string | null>(
        null,
    );
    const [detailSegmentId, setDetailSegmentId] = useState<string | null>(null);
    const [detailShiftId, setDetailShiftId] = useState<string | null>(null);
    const anchorDateKey = anchorOverrideKey ?? todayKey;
    const monthStartKey = anchorDateKey
        ? startOfMonthDateKey(anchorDateKey)
        : null;
    const monthEndKey = monthStartKey ? endOfMonthDateKey(monthStartKey) : null;

    const { data, error, isLoading } = useCaregiverAvailabilityProfile(
        organizationId,
        caregiverId,
        monthStartKey,
        monthEndKey,
    );

    const dayGroups = useMemo(() => {
        if (!data || !timezone || !monthStartKey || !monthEndKey) {
            return [];
        }
        const items: Array<TimelineItem> = [
            ...buildEffectiveAvailabilityItems(
                data.availability,
                data.weekly_availability,
                externalSourceLabel,
                monthStartKey,
                monthEndKey,
                timezone,
            ),
        ];
        data.shifts.forEach((shift) => {
            items.push({
                kind: 'shift',
                id: `shift-${shift.id}`,
                type: 'shift',
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                details: shift.patient_name,
            });
        });
        return buildTimeboxedDayGroups(items, timezone);
    }, [data, externalSourceLabel, timezone, monthStartKey, monthEndKey]);

    const detailSegment = useMemo(
        () => findAvailabilitySegmentById(dayGroups, detailSegmentId),
        [dayGroups, detailSegmentId],
    );

    const detailShift = useMemo(
        () => data?.shifts.find((shift) => shift.id === detailShiftId) ?? null,
        [data, detailShiftId],
    );

    if (!timezone || !monthStartKey || !todayKey) {
        return (
            <div className="flex h-40 items-center justify-center">
                <Spinner className="size-5" />
            </div>
        );
    }

    return (
        <div className="min-w-0">
            <h2 className="mb-3 flex min-h-8 items-center text-base font-semibold">
                Overview
            </h2>
            <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.75fr)]">
                <div className="flex min-w-0 flex-col gap-2">
                    <div className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            aria-label="Previous month"
                            onClick={() =>
                                setAnchorOverrideKey(
                                    addMonthsToDateKey(monthStartKey, -1),
                                )
                            }
                        >
                            <ChevronLeft className="size-3.5" />
                        </Button>
                        <span className="text-xs font-medium">
                            {formatMonthKey(monthStartKey, timezone)}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            aria-label="Next month"
                            onClick={() =>
                                setAnchorOverrideKey(
                                    addMonthsToDateKey(monthStartKey, 1),
                                )
                            }
                        >
                            <ChevronRight className="size-3.5" />
                        </Button>
                        {isLoading && <Spinner className="ml-auto size-4" />}
                    </div>
                    {error ? (
                        <div
                            className="text-destructive flex min-h-40 items-center justify-center text-sm"
                            role="alert"
                        >
                            Failed to load caregiver schedule.
                        </div>
                    ) : (
                        <AvailabilityMonthGrid
                            monthStartKey={monthStartKey}
                            todayKey={todayKey}
                            timezone={timezone}
                            dayGroups={dayGroups}
                            onAvailabilityClick={(segment) =>
                                setDetailSegmentId(segment.id)
                            }
                            onShiftClick={(shiftItemId) =>
                                setDetailShiftId(
                                    shiftItemId.replace(/^shift-/u, ''),
                                )
                            }
                        />
                    )}
                </div>
                <aside className="flex min-w-0 flex-col">
                    <ProfileNotesPanel
                        organizationId={organizationId}
                        entityType="caregiver"
                        entityId={caregiverId}
                        entityFirstName={caregiverFirstName}
                    />
                </aside>
            </div>
            {/* Read-only: editing lives in the Availability tab. */}
            <AvailabilityDetailsDialog
                segment={detailSegment}
                timezone={timezone}
                onClose={() => setDetailSegmentId(null)}
            />
            <CaregiverShiftDetailsDialog
                shift={detailShift}
                timezone={timezone}
                onClose={() => setDetailShiftId(null)}
            />
        </div>
    );
}
